import { i18nTranslationHelper } from '@/i18n'
import FieldLabelTooltip from '@/presentation/components/ui/input/field-label-tooltip'
import { SelectProps } from '@mui/material'
import { ReactNode } from 'react'
import { Controller, FieldValues, Path, UseControllerProps, useFormContext } from 'react-hook-form'
import Input from './input'

export type SelectFieldOptionType<TValue> = {
  label: string
  value: TValue
  disabled?: boolean
}

export type SelectFieldProps<TField extends FieldValues, TValue> = {
  name: Path<TField>
  label: string | ReactNode
  tooltipMessage?: string
  options?: SelectFieldOptionType<TValue>[]
  size?: 'small' | 'medium'
  errorMessage?: string
  displayEmpty?: boolean
  emptyLabel?: string
  helperText?: string | null
  placeholder?: string
} & SelectProps &
  UseControllerProps<TField>

export default function SelectField<TField extends FieldValues, TValue>({
  name,
  control,
  required,
  disabled,
  rules = { required },
  label,
  tooltipMessage,
  size = 'small',
  multiple,
  emptyLabel,
  className,
  ...props
}: SelectFieldProps<TField, TValue>) {
  const formContext = useFormContext<TField>()

  const t = i18nTranslationHelper.getTValidations
  if (!emptyLabel) {
    emptyLabel = multiple ? t('requiredAtLeastOneOption') : t('requiredOption')
  }

  const i18nRules = {
    ...rules,
    required: required ? emptyLabel : false
  }

  const finalLabel = tooltipMessage ? (
    <FieldLabelTooltip label={label} toolTipTitle={tooltipMessage} />
  ) : (
    label
  )

  return (
    <div className={`text-start w-full ${className}`}>
      <Controller
        name={name}
        control={formContext?.control ?? control}
        rules={i18nRules}
        render={inputProps => (
          <Input
            label={finalLabel}
            name={name}
            required={required}
            disabled={disabled}
            multiple={multiple}
            rules={i18nRules}
            emptyLabel={emptyLabel}
            size={size}
            slotProps={{
              input: {
                'data-testid': `select-field-${name}`
              } as object
            }}
            {...props}
            {...inputProps}
          />
        )}
      />
    </div>
  )
}
